import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, QrCode, Search, Package, MapPin, Thermometer, Calendar, AlertTriangle, ArrowRightLeft, ShoppingCart, ScanLine, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileScannerPage() {
  const navigate = useNavigate();
  const [lpNumber, setLpNumber] = useState('');
  const [palletInfo, setPalletInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [error, setError] = useState('');
  const [showAction, setShowAction] = useState(null); // 'hold', 'allocate', 'transfer'
  const [actionForm, setActionForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const lookupLP = useCallback(async (lp) => {
    if (!lp.trim()) return;
    setLoading(true);
    setError('');
    setPalletInfo(null);
    setTransactions([]);
    try {
      const res = await axios.get(`${API}/warehouse/license-plates/${lp.trim()}`);
      setPalletInfo(res.data);
      // Fetch transactions
      try {
        const txRes = await axios.get(`${API}/warehouse/license-plates/${lp.trim()}/transactions`);
        setTransactions(txRes.data || []);
      } catch {}
    } catch (err) {
      setError(err.response?.status === 404 ? 'License plate not found' : 'Error looking up license plate');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    lookupLP(lpNumber);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        scanQR();
      }
    } catch {
      toast.error('Camera not available');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setScanning(false);
  };

  const scanQR = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;

    const tick = () => {
      if (!scanning && !videoRef.current?.srcObject) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        try {
          // Using jsQR if available
          if (window.jsQR) {
            const code = window.jsQR(imageData.data, imageData.width, imageData.height);
            if (code) {
              setLpNumber(code.data);
              lookupLP(code.data);
              stopCamera();
              return;
            }
          }
        } catch {}
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  // Fetch active work orders for "Assign to WO" action
  useEffect(() => {
    axios.get(`${API}/work-orders`).then(res => {
      setWorkOrders((res.data || []).filter(wo => wo.status === 'in_production' || wo.status === 'staged'));
    }).catch(() => {});
  }, []);

  const handleAction = async (action) => {
    setActionLoading(true);
    const lp = palletInfo?.lp_number;
    try {
      if (action === 'hold') {
        await axios.post(`${API}/warehouse/license-plates/${lp}/hold`, {
          reason: actionForm.reason || 'QA Hold',
          notes: actionForm.notes || ''
        });
        toast.success('License plate placed on hold');
      } else if (action === 'allocate') {
        await axios.post(`${API}/warehouse/license-plates/${lp}/allocate`, {
          order_number: actionForm.order_number || ''
        });
        toast.success('Allocated to order');
      } else if (action === 'transfer') {
        await axios.post(`${API}/warehouse/license-plates/${lp}/transfer`, {
          new_location_id: actionForm.new_location_id || ''
        });
        toast.success('Transferred to new location');
      } else if (action === 'assign_wo') {
        // Assign LP to a work order via allocate-lot
        const woId = actionForm.wo_id;
        const compId = actionForm.component_id;
        if (!woId || !compId) {
          toast.error('Select a work order and component');
          setActionLoading(false);
          return;
        }
        await axios.post(`${API}/production/${woId}/allocate-lot`, {
          component_id: compId,
          lot_number: palletInfo?.lot_number || lp,
          allocated_qty: palletInfo?.quantity || palletInfo?.quantity_on_hand || 0,
        });
        toast.success(`Assigned ${lp} to work order`);
      }
      setShowAction(null);
      setActionForm({});
      lookupLP(lp);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} /> Back
      </button>

      <div className="mobile-page-title" style={{ marginBottom: 16 }}>Scan License Plate</div>

      {/* Camera Scanner */}
      {!palletInfo && (
        <>
          <div className="mobile-scanner-container" style={{ marginBottom: 16 }}>
            <video ref={videoRef} style={{ display: scanning ? 'block' : 'none' }} playsInline muted />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {!scanning && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white', gap: 12 }}>
                <QrCode size={48} style={{ opacity: 0.5 }} />
                <button className="mobile-action-btn primary" style={{ width: 'auto', padding: '10px 24px', fontSize: 14 }} onClick={startCamera}>
                  <ScanLine size={18} /> Start Camera
                </button>
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center', color: '#8e8e93', fontSize: 13, marginBottom: 12 }}>or enter LP number manually</div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              className="mobile-input"
              placeholder="LP-YYYYMMDD-XXXX"
              value={lpNumber}
              onChange={(e) => setLpNumber(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="mobile-action-btn primary" type="submit" style={{ width: 'auto', padding: '12px 18px' }} disabled={loading}>
              <Search size={18} />
            </button>
          </form>
        </>
      )}

      {/* Loading */}
      {loading && <div className="mobile-loading"><div className="mobile-spinner" /></div>}

      {/* Error */}
      {error && (
        <div style={{ background: '#fce4ec', padding: 14, borderRadius: 12, textAlign: 'center', color: '#c62828', fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Pallet Info */}
      {palletInfo && (
        <>
          <div className="mobile-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1c1c1e' }}>{palletInfo.lp_number}</div>
              <span className={`mobile-status-badge ${palletInfo.status === 'active' ? 'in_production' : palletInfo.status === 'on_hold' ? 'on_hold' : 'scheduled'}`}>
                {palletInfo.status}
              </span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label"><Package size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />Item</span>
              <span className="mobile-detail-value">{palletInfo.item_name || 'N/A'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">Lot Number</span>
              <span className="mobile-detail-value">{palletInfo.lot_number || 'N/A'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">Quantity</span>
              <span className="mobile-detail-value">{palletInfo.quantity || palletInfo.quantity_on_hand || 'N/A'} {palletInfo.uom || 'lbs'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label"><MapPin size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />Location</span>
              <span className="mobile-detail-value">{palletInfo.location_code || palletInfo.location_id || 'N/A'}</span>
            </div>
            {palletInfo.temperature_reading && (
              <div className="mobile-detail-row">
                <span className="mobile-detail-label"><Thermometer size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />Temperature</span>
                <span className="mobile-detail-value">{palletInfo.temperature_reading}°F</span>
              </div>
            )}
            {palletInfo.expiration_date && (
              <div className="mobile-detail-row">
                <span className="mobile-detail-label"><Calendar size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />Expiration</span>
                <span className="mobile-detail-value">{palletInfo.expiration_date}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <button className="mobile-action-btn danger" style={{ fontSize: 13, padding: '10px 8px' }} onClick={() => setShowAction('hold')}>
              <AlertTriangle size={16} /> Hold
            </button>
            <button className="mobile-action-btn primary" style={{ fontSize: 13, padding: '10px 8px' }} onClick={() => setShowAction('allocate')}>
              <ShoppingCart size={16} /> Allocate
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <button className="mobile-action-btn" style={{ fontSize: 13, padding: '10px 8px', background: '#5ac8fa' }} onClick={() => setShowAction('transfer')}>
              <ArrowRightLeft size={16} /> Transfer
            </button>
            <button className="mobile-action-btn" style={{ fontSize: 13, padding: '10px 8px', background: '#5856d6' }} onClick={() => setShowAction('assign_wo')}>
              <ClipboardList size={16} /> Assign to WO
            </button>
          </div>

          {/* Scan Another */}
          <button
            className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#007aff', marginBottom: 16 }}
            onClick={() => { setPalletInfo(null); setTransactions([]); setLpNumber(''); setError(''); }}
          >
            Scan Another
          </button>

          {/* Event Log */}
          {transactions.length > 0 && (
            <div className="mobile-detail-section">
              <div className="mobile-detail-section-title">Event Log</div>
              {transactions.slice(0, 10).map((tx, i) => (
                <div key={i} className="mobile-activity-item">
                  <div className="mobile-activity-dot" style={{
                    background: tx.transaction_type === 'receipt' ? '#34c759'
                      : tx.transaction_type === 'hold' ? '#ff3b30'
                      : tx.transaction_type === 'transfer' ? '#5ac8fa'
                      : '#8e8e93'
                  }} />
                  <div className="mobile-activity-content">
                    <div className="mobile-activity-text">{tx.transaction_type} {tx.notes ? `- ${tx.notes}` : ''}</div>
                    <div className="mobile-activity-time">
                      {tx.timestamp ? new Date(tx.timestamp).toLocaleString() : ''}
                      {tx.user ? ` · ${tx.user}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Action Sheets */}
      {showAction && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAction(null); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">
              {showAction === 'hold' ? 'QA Hold' : showAction === 'allocate' ? 'Allocate to Order' : showAction === 'assign_wo' ? 'Assign to Work Order' : 'Transfer Location'}
            </div>

            {showAction === 'hold' && (
              <>
                <div className="mobile-input-group">
                  <label className="mobile-input-label">Reason</label>
                  <input className="mobile-input" placeholder="Hold reason" value={actionForm.reason || ''} onChange={e => setActionForm(f => ({ ...f, reason: e.target.value }))} />
                </div>
                <div className="mobile-input-group">
                  <label className="mobile-input-label">Notes</label>
                  <input className="mobile-input" placeholder="Additional notes" value={actionForm.notes || ''} onChange={e => setActionForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </>
            )}
            {showAction === 'allocate' && (
              <div className="mobile-input-group">
                <label className="mobile-input-label">Order Number</label>
                <input className="mobile-input" placeholder="SO-XXXX" value={actionForm.order_number || ''} onChange={e => setActionForm(f => ({ ...f, order_number: e.target.value }))} />
              </div>
            )}
            {showAction === 'transfer' && (
              <div className="mobile-input-group">
                <label className="mobile-input-label">New Location ID</label>
                <input className="mobile-input" placeholder="Location ID" value={actionForm.new_location_id || ''} onChange={e => setActionForm(f => ({ ...f, new_location_id: e.target.value }))} />
              </div>
            )}
            {showAction === 'assign_wo' && (
              <>
                <div style={{ padding: 10, background: '#f0f9ff', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                  <strong>{palletInfo?.lp_number}</strong> · {palletInfo?.item_name}<br/>
                  Lot: {palletInfo?.lot_number} · Qty: {palletInfo?.quantity || palletInfo?.quantity_on_hand}
                </div>
                <div className="mobile-input-group">
                  <label className="mobile-input-label">Work Order</label>
                  <select className="mobile-input" value={actionForm.wo_id || ''} onChange={e => { setActionForm(f => ({ ...f, wo_id: e.target.value, component_id: '' })); }}>
                    <option value="">Select work order...</option>
                    {workOrders.map(wo => (
                      <option key={wo.wo_id} value={wo.wo_id}>{wo.wo_number} - {wo.item_name} ({wo.status})</option>
                    ))}
                  </select>
                </div>
                {actionForm.wo_id && (
                  <div className="mobile-input-group">
                    <label className="mobile-input-label">BOM Component</label>
                    <select className="mobile-input" value={actionForm.component_id || ''} onChange={e => setActionForm(f => ({ ...f, component_id: e.target.value }))}>
                      <option value="">Select component...</option>
                      {(workOrders.find(w => w.wo_id === actionForm.wo_id)?.bom_usage || []).map(comp => (
                        <option key={comp.component_id} value={comp.component_id}>{comp.component_name} ({comp.unit})</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366' }} onClick={() => { setShowAction(null); setActionForm({}); }}>
                Cancel
              </button>
              <button
                className={`mobile-action-btn ${showAction === 'hold' ? 'danger' : 'primary'}`}
                onClick={() => handleAction(showAction)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
